<script setup lang="ts">
import { generateUUID } from '@/Services/helpers';
import { useLayoutStore } from '@/Stores/layoutStore';
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { PaperClipIcon, TagIcon } from '@heroicons/vue/20/solid';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { route } from 'ziggy-js';
const store = useLayoutStore();

import type { SharedProps } from '@/Types/inertia';

import { usePage } from '@inertiajs/vue3';

const page = usePage<SharedProps>();

//import InputNumber from "primevue/inputnumber";
import Knob from 'primevue/knob';
import Slider from 'primevue/slider';

const handleFocus = () => store.setFocus(true);
//const handleBlur = () => store.setFocus(false);

const channelHash = ref('');
const submittedJapanese = ref('');

const isProcessing = ref(false);
const elapsedTime = ref(0);
const totalProcessingTime = ref<number | null>(null);
const interval = ref<number | null>(null);
const parsedHandle = ref<string | null>(null);
const outputId = ref<number | null>(null);

const chunkCounter = ref(0);
const chunkTotal = ref(0);
const speechCompleted = ref(0);
const speechTotal = ref(0);

const locale = page.props.locale;

//const handle = ref("");

//const magic = ref(50);

interface HashResponse {
    length: number;
    outputId: number;
}

const labels = [
    { name: 'Romaji', value: 'romaji' },
    { name: 'Hiragana', value: 'hiragana' },
];

const labelled = ref(labels[0]);

const onFormSubmit = async () => {
    if (!submittedJapanese.value.trim()) return;

    channelHash.value = generateUUID();
    parsedHandle.value = null;
    elapsedTime.value = 0;
    totalProcessingTime.value = null;
    isProcessing.value = true;

    if (interval.value) clearInterval(interval.value);
    interval.value = setInterval(() => {
        elapsedTime.value++;
    }, 1000);

    const { data } = await axios.post<HashResponse>(route('conversion.submit'), {
        text: submittedJapanese.value,
        //label: labelled.value.value,
        channelHash: channelHash.value,
    });

    console.log('Submitted', data);

    outputId.value = data.outputId;
    listenForEvent(outputId.value);
};

const listenForEvent = (outputId: number) => {
    const channelName = `Mecab.Created.${outputId}`;

    console.log('channelName:' + channelName);

    window.Echo.private(channelName).listen('.mecab.created', (event: any) => {
        console.log('Received mecab.created event:', event);

        // Stop processing
        isProcessing.value = false;
        if (interval.value) clearInterval(interval.value);

        // Store the parsed handle & final time

        totalProcessingTime.value = elapsedTime.value;
    });

    const channelName2 = `SentenceSpeech.Added.${outputId}`;
    window.Echo.private(channelName2).listen('.sentence-speech.added', (event: any) => {
        console.log('Received SentenceSpeech.Added Event:', event);
        speechCompleted.value = event.counter;
        speechTotal.value = event.count;
    });

    // FAIL
    const channelName3 = `SpeechFiles.Combined.${outputId}`;
    window.Echo.private(channelName3).listen('.speech-files.combined', (event: any) => {
        console.log('SpeechFiles.Combined', event);
        parsedHandle.value = event.handle;

        router.visit(
            route('conversion.show', {
                conversion: parsedHandle.value,
                locale: locale,
            }),
        );
    });

    const channelName4 = `Walk.Chunked.${outputId}`;
    window.Echo.private(channelName4).listen('.walk.chunked', (event: any) => {
        console.log('SpeechFiles.Combined', event);
        chunkTotal.value = event.total;
        chunkCounter.value = event.counter;
    });

    console.log(`Listening on channel: ${channelName}`);
    console.log(`Listening on channel: ${channelName2}`);
    console.log(`Listening on channel: ${channelName3}`);

    /*
        // ✅ Listen for full failures (Mecab completely failed)
        const failureChannel = `failed.${outputId}`;
        window.Echo.private(failureChannel).listen(".mecab.failed", (event: any) => {
            console.error("Mecab process completely failed:", event);
            isProcessing.value = false;
            totalProcessingTime.value = null; // Reset
            parsedHandle.value = null;
            chunkTotal.value = 0;
            chunkCompleted.value = 0;
            alert(`Error: ${event.errorMessage}`);
        });

        // ✅ Listen for warnings (Mecab succeeded but some parts failed)
        window.Echo.private(failureChannel).listen(".mecab.warning", (event: any) => {
            console.warn("Mecab processed with warnings:", event);
            alert(`Warning: ${event.warningMessage}`);
        });


        console.log(`Listening for failures on channel: ${failureChannel}`);

         */
};

onMounted(() => {
    return () => {
        if (interval.value) clearInterval(interval.value);
    };
});

const sliderValue = ref(0);
// Quadratic scaling: (sliderValue²) / 5
const computedValue = computed({
    get: () => Math.ceil(Math.pow(sliderValue.value, 2.5) / 264.9), // Cubic formula, always rounds up
    set: (val) => (sliderValue.value = Math.sqrt(val * 5)), // Inverse function
});

const computedValueString = computed(() => computedValue.value + '');

// ✅ Zipf-like coverage function: coverage = 60 + 2.206 / (x - 16.077)^-0.406
const coverage = computed(() => {
    const x = computedValue.value;

    if (x < 1000) {
        // Zipf-like formula (no offset, starts at x = 0)
        return 60 + 2.206 / Math.pow(x + 1, -0.406);
    } else {
        // Linear increase from ~96.5 at 1000 to 99.99 at 2000
        return 96.46 + ((x - 1000) * (99.99 - 96.5)) / 1000;
    }
});
</script>

<template>
    <div class="p-10">
        <div class="mt-4 mb-4">
            <div v-if="isProcessing" class="mt-2 text-center text-gray-600">Processing... ({{ elapsedTime }}s)</div>
            <div v-if="totalProcessingTime !== null" class="mt-2 text-center text-gray-500">Processing took {{ totalProcessingTime }} seconds.</div>
            <div v-if="parsedHandle" class="mt-4 rounded border border-gray-300 bg-gray-100 p-2 text-center">
                <strong>Handle:</strong> {{ parsedHandle }}
            </div>

            <p class="mt-2 text-lg font-medium tracking-tight text-white">Data</p>
            <!-- Processing message -->
            <div v-if="isProcessing" class="mt-2 text-gray-600">Processing... ({{ elapsedTime }}s)</div>

            <!-- Show final processing time -->
            <div v-if="totalProcessingTime !== null" class="mt-2 text-gray-500">Processing took {{ totalProcessingTime }} seconds.</div>

            <!-- Show handle when received -->
            <div v-if="parsedHandle" class="mt-4 rounded border border-gray-300 bg-gray-100 p-2"><strong>Handle:</strong> {{ parsedHandle }}</div>

            <div v-if="chunkTotal" class="mt-4 rounded border border-gray-300 bg-gray-100 p-2">
                <strong>Chunks:</strong>{{ chunkCounter }} / {{ chunkTotal }}
            </div>

            <div v-if="speechTotal" class="mt-4 rounded border border-gray-300 bg-gray-100 p-2">
                <strong>Speech:</strong>{{ speechCompleted }} / {{ speechTotal }}
            </div>
        </div>
        <div class="m-4">
            {{ locale }}
            <Knob v-model="coverage" rangeColor="orange" valueColor="MediumTurquoise" :max="100" :valueTemplate="computedValueString" readonly />
            <!--
            <InputNumber type="text" v-model="sliderValue" class="m-4" />

            <InputNumber type="text" v-model="coverage" class="m-4" />

            <InputNumber type="text" v-model="computedValue" class="m-4" />
            -->
            <Slider v-model="sliderValue" :max="200" />
        </div>

        <form @submit.prevent="onFormSubmit" class="relative">
            <div
                class="rounded-lg bg-white outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600"
            >
                <label for="title" class="sr-only">Japanese Text</label>
                <textarea
                    v-model="submittedJapanese"
                    rows="10"
                    id="title"
                    class="block w-full px-3 pt-2.5 text-lg font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none"
                    placeholder="Enter Japanese text here..."
                    @focus="handleFocus"
                />
            </div>

            <div class="absolute inset-x-px bottom-0">
                <div class="flex flex-nowrap justify-end space-x-2 px-2 py-2 sm:px-3">
                    <Listbox as="div" v-model="labelled" class="shrink-0">
                        <ListboxLabel class="sr-only">Select Output Format</ListboxLabel>
                        <div class="relative">
                            <ListboxButton
                                class="relative inline-flex items-center rounded-full bg-gray-50 px-2 py-2 text-sm font-medium whitespace-nowrap text-gray-500 hover:bg-gray-100 sm:px-3"
                            >
                                <TagIcon
                                    :class="[labelled === null ? 'text-gray-300' : 'text-gray-500', 'size-5 shrink-0 sm:-ml-1']"
                                    aria-hidden="true"
                                />
                                <span :class="[labelled === null ? '' : 'text-gray-900', 'hidden truncate sm:ml-2 sm:block']">{{
                                    labelled === null ? 'Label' : labelled.name
                                }}</span>
                            </ListboxButton>

                            <transition
                                leave-active-class="transition ease-in duration-100"
                                leave-from-class="opacity-100"
                                leave-to-class="opacity-0"
                            >
                                <ListboxOptions
                                    class="absolute right-0 z-10 mt-1 max-h-56 w-52 overflow-auto rounded-lg bg-white py-3 text-base shadow-sm outline-1 outline-black/5 sm:text-sm"
                                >
                                    <ListboxOption as="template" v-for="label in labels" :key="label.value" :value="label" v-slot="{ active }">
                                        <li
                                            :class="[
                                                active ? 'relative bg-gray-100 hover:outline-hidden' : 'bg-white',
                                                'cursor-default px-3 py-2 select-none',
                                            ]"
                                        >
                                            <div class="flex items-center">
                                                <span class="block truncate font-medium">{{ label.name }}</span>
                                            </div>
                                        </li>
                                    </ListboxOption>
                                </ListboxOptions>
                            </transition>
                        </div>
                    </Listbox>
                </div>

                <div class="flex items-center justify-between space-x-3 border-t border-gray-200 px-2 py-2 sm:px-3">
                    <div class="flex">
                        <button type="button" class="group -my-2 -ml-2 inline-flex items-center rounded-full px-3 py-2 text-left text-gray-400">
                            <PaperClipIcon class="mr-2 -ml-1 size-5 group-hover:text-gray-500" aria-hidden="true" />
                            <span class="text-sm text-gray-500 italic group-hover:text-gray-600">Attach a file</span>
                        </button>
                    </div>
                    <div class="shrink-0">
                        <button
                            type="submit"
                            class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                            Convert
                        </button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<style scoped></style>
